/* ============================================================
   Lane Linen — Sheets collection redesign
   Fetches sheet products from the Storefront API, groups the
   one-product-per-colour catalogue back into product lines,
   renders fabric-family rows, and drives fully client-side
   facets + sort. Add-to-bag posts to Dawn's /cart/add.js and
   refreshes the theme cart drawer.
   ============================================================ */
(function () {
  "use strict";

  /* i18n: window.LL_T (snippets/ll-js-i18n.liquid) maps English UI text to the
     current language; English is the fallback. Internal values stay English. */
  var LLT = window.LL_T || {};
  function llT(s, n) { var t = LLT[s] || s; return n != null ? t.replace("{n}", n) : t; }
  /* Display label for fabric/category/colour values ("450 TC Cotton" has a number in it). */
  /* Translated product titles by product id (filled by App.fetch on non-English pages). */
  var LLNAMES = {};
  function llLabel(s) { var m = /^(\d+) TC Cotton$/.exec(s || ""); return m ? llT("{n} TC Cotton", m[1]) : llT(s); }

  var COLOR_MAP = {
    black: "#0d0d0d", "black-dots": "#0d0d0d", charcoal: "#3a3a3a",
    "charcoal swiss dot": "#3a3a3a", "dark denim": "#243b53",
    "estate blue": "#1e3a5f", "estate blue swiss dot": "#1e3a5f",
    "french blue": "#5f7fa3", "french blue swiss dot": "#5f7fa3",
    ivory: "#f2ead8", "ivory swiss dot": "#f2ead8", linen: "#d8cdb4",
    "linen swiss dot": "#d8cdb4", mineral: "#a8b0ae", "mineral swiss dot": "#a8b0ae",
    seafoam: "#b7d4c9", "sepia rose": "#c9a29a", silver: "#c8c8c8",
    taupe: "#a89583", white: "#f6f4ef", "white swiss dot": "#f6f4ef",
    grey: "#8a8a8a", gray: "#8a8a8a", moonstruck: "#cfd3da"
  };
  function colorForName(name) {
    if (!name) return "#c8bfb5";
    return COLOR_MAP[name.trim().toLowerCase()] || "#c8bfb5";
  }
  function extractThreadCount(text) {
    var m = (text || "").match(/(\d{3,4})\s*thread\s*count/i);
    return m ? Number(m[1]) : null;
  }
  function fabricLabelFor(node) {
    var t = (node.title || "").toLowerCase();
    var tags = (node.tags || []).map(function (x) { return x.toLowerCase(); });
    if (t.indexOf("organic") !== -1) return "Percale Weave";
    // Before the "linen" check: Flannel comes in a colour called Linen.
    if (t.indexOf("flannel") !== -1 || tags.indexOf("flannel") !== -1) return "Flannel";
    if (t.indexOf("sateen") !== -1 || tags.indexOf("sateen") !== -1) return "Sateen Weave";
    if (t.indexOf("linen") !== -1) return "Washed Linen";
    if (t.indexOf("bamboo") !== -1) return "Bamboo Viscose";
    if (t.indexOf("percale") !== -1) return "Percale Weave";
    var tc = extractThreadCount(node.title);
    if (tc) return tc + " TC Cotton";
    return "Premium Cotton";
  }
  function categoryLabelFor(node) {
    var t = (node.title || "").toLowerCase();
    if (t.indexOf("beach") !== -1) return "Beach";
    if (t.indexOf("pillowcase") !== -1) return "Pillowcases";
    if (t.indexOf("sheet") !== -1) return "Sheets";
    if (t.indexOf("towel") !== -1) return "Towels";
    return "More";
  }
  function normalizeSize(v) {
    var s = String(v || "").trim();
    if (s.toLowerCase() === "cal king") return "California King";
    return s;
  }
  /* Card label only: lead bed sheet set names with their fibre content.
     baseTitle stays untouched because grouping, sorting and hero colour key off it. */
  function displayTitle(base) {
    var s = base.toLowerCase();
    if (s.indexOf("sheet set") === -1 || s.indexOf("bath") !== -1 || s.indexOf("100%") !== -1) return base;
    if (/^(egyptian|organic) cotton/.test(s)) return "100% " + base;
    return "100% Cotton " + base;
  }
  function splitTitle(title) {
    var idx = title.lastIndexOf(" - ");
    if (idx === -1) return { base: title.trim(), color: "" };
    return { base: title.slice(0, idx).trim(), color: title.slice(idx + 3).trim() };
  }
  function money(amount, currency) {
    var num = parseFloat(amount);
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency", currency: currency,
        maximumFractionDigits: num % 1 === 0 ? 0 : 2
      }).format(num);
    } catch (e) { return currency + " " + num.toFixed(2); }
  }
  function numericId(gid) { return (gid || "").split("/").pop(); }

  function metafieldColor(node) {
    var mf = node.colorName;
    return (mf && mf.value) ? mf.value : "";
  }

  /* Main-colour buckets for the compact swatch filter. Each specific colour name
     maps into one bucket. Empty buckets are hidden automatically. */
  var MAIN_COLORS = [
    { name: "Black",  hex: "#1a1a1a", colors: ["Black"] },
    { name: "White",  hex: "#ffffff", colors: ["White", "Dot White", "Snowdrop", "White Plaid"] },
    { name: "Cream",  hex: "#f3ead3", colors: ["Ivory", "Dot Ivory", "Natural", "Vanilla", "Linen"] },
    { name: "Beige",  hex: "#d9c7a3", colors: [] },
    { name: "Grey",   hex: "#8f8f8f", colors: ["Silver", "Glacier Grey", "Haze Grey", "Paloma Grey", "Moonstruck", "Charcoal"] },
    { name: "Blue",   hex: "#2f4b9b", colors: ["Blue", "Cool Blue", "Cobalt Blue", "Estate Blue", "French Blue", "Navy", "Dark Denim"] },
    { name: "Green",  hex: "#3f7d5a", colors: ["Mineral", "Buffalo Check Green"] },
    { name: "Brown",  hex: "#6b4a2b", colors: ["Taupe"] },
    { name: "Pink",   hex: "#e2a4c0", colors: ["Rose Dawn", "Sepia Rose", "Orchid Petal", "Lilac"] },
    { name: "Red",    hex: "#b83232", colors: ["Mineral Red", "Buffalo Check Red"] },
    { name: "Orange", hex: "#d8692a", colors: [] },
    { name: "Yellow", hex: "#e9c84a", colors: ["Cocoon"] },
    { name: "Multi",  multi: true,   colors: ["Black Dots", "Chevron Cool Blue", "Polka Dot Black", "Polka Dot Blue", "Ticking Stripe", "Trellis Silver", "Windowpane", "Windowpane Charcoal", "Dot Mineral", "Misty Dot"] }
  ];
  var MAIN_BY_NAME = {};
  MAIN_COLORS.forEach(function (m) { MAIN_BY_NAME[m.name] = m; });
  // Manual name → bucket (from the curated lists above). Takes precedence over hex matching.
  var MANUAL_NAME_TO_MAIN = {};
  MAIN_COLORS.forEach(function (m) { m.colors.forEach(function (c) { MANUAL_NAME_TO_MAIN[c.toLowerCase()] = m.name; }); });

  function hexToRgb(h) {
    h = String(h || "").trim().replace("#", "");
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6) return null;
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  /* Resolve a colour to a main bucket: curated name map first, then nearest bucket by hex.
     Makes the colour filter work on any collection without hand-mapping every name. */
  function mainForColor(name, hex) {
    var manual = MANUAL_NAME_TO_MAIN[String(name || "").toLowerCase()];
    if (manual) return manual;
    var rgb = hexToRgb(hex);
    if (!rgb) return null;
    var best = null, bd = Infinity;
    MAIN_COLORS.forEach(function (m) {
      if (m.multi || !m.hex) return;
      var mr = hexToRgb(m.hex);
      var d = (rgb[0] - mr[0]) * (rgb[0] - mr[0]) + (rgb[1] - mr[1]) * (rgb[1] - mr[1]) + (rgb[2] - mr[2]) * (rgb[2] - mr[2]);
      if (d < bd) { bd = d; best = m.name; }
    });
    return best;
  }

  function groupByLine(products) {
    var map = {};
    var order = [];
    products.forEach(function (p) {
      var node = p.node;
      var parsed = splitTitle(node.title);
      var color = metafieldColor(node) || parsed.color || "Default";
      var key = parsed.base.toLowerCase();
      var hex = (node.productsColor && node.productsColor.value) ? node.productsColor.value : colorForName(color);
      var swatch = { colorName: color, hex: hex, product: p };
      if (!map[key]) {
        order.push(key);
        var ratingVal = null;
        if (node.rating && node.rating.value) {
          try { ratingVal = parseFloat(JSON.parse(node.rating.value).value); } catch (e) { ratingVal = null; }
        }
        var ratingCount = (node.ratingCount && node.ratingCount.value) ? parseInt(node.ratingCount.value, 10) : 0;
        map[key] = {
          baseTitle: parsed.base,
          displayBase: LLNAMES[node.id] ? splitTitle(LLNAMES[node.id]).base : null,
          threadCount: extractThreadCount(parsed.base) || extractThreadCount(node.title),
          fabricLabel: fabricLabelFor(node),
          categoryLabel: categoryLabelFor(node),
          minPrice: node.priceRange.minVariantPrice,
          rating: ratingVal,
          ratingCount: ratingCount || 0,
          representative: p,
          colors: [swatch]
        };
      } else {
        map[key].colors.push(swatch);
        if (parseFloat(node.priceRange.minVariantPrice.amount) < parseFloat(map[key].minPrice.amount)) {
          map[key].minPrice = node.priceRange.minVariantPrice;
        }
      }
    });
    return order.map(function (k) { return map[k]; });
  }

  function rankBase(t) {
    var s = t.toLowerCase();
    if (s.indexOf("sheet set") !== -1) return 0;
    if (s.indexOf("fitted sheet") !== -1) return 1;
    if (s.indexOf("flat sheet") !== -1) return 2;
    if (s.indexOf("pillowcase") !== -1) return 3;
    return 4;
  }

  /* Merchandised order inside a fabric family under the "Featured" sort.
     Anything not listed here falls in after these, alphabetically. */
  var FEATURED_ORDER = [
    "1000 thread count",
    "egyptian cotton",
    "450 thread count",
    "600 thread count"
  ];
  function featuredRank(t) {
    var s = (t || "").toLowerCase();
    for (var i = 0; i < FEATURED_ORDER.length; i++) {
      if (s.indexOf(FEATURED_ORDER[i]) !== -1) return i;
    }
    return FEATURED_ORDER.length;
  }

  /* Card hover image: a "_hover" shot if the product has one, otherwise the first
     image that isn't another folded stack (2nd stack / 3-piece / 1-pillow shots),
     so hovering shows the sheets on a bed rather than a near-duplicate. */
  var STACK_RE = /pillow[-_ ]?stack|main-1-pillow|-p[34]\.|folded/i;
  function hoverImageUrl(node) {
    var imgs = node.images.edges.map(function (e) { return e.node; });
    if (imgs.length < 2) return "";
    for (var i = 1; i < imgs.length; i++) {
      if (/_hover\./i.test(imgs[i].url)) return imgs[i].url;
    }
    for (var j = 1; j < imgs.length; j++) {
      if (!STACK_RE.test(imgs[j].url.split("?")[0] + " " + (imgs[j].altText || ""))) return imgs[j].url;
    }
    return imgs[1].url;
  }

  /* Lines that get a "Best Seller" tag on their card. Case-insensitive substring of the line title. */
  var BEST_SELLER_LINES = ["organic cotton sheet set"];
  function isBestSeller(baseTitle) {
    var s = (baseTitle || "").toLowerCase();
    return BEST_SELLER_LINES.some(function (t) { return s.indexOf(t) !== -1; });
  }

  /* Lines whose hero image should be a specific colourway rather than the lightest
     one. Matched as a case-insensitive substring on both sides. */
  var LINE_HERO_COLOR = {
    "egyptian cotton": "mineral"
  };
  function heroColorFor(baseTitle) {
    var s = (baseTitle || "").toLowerCase();
    for (var k in LINE_HERO_COLOR) {
      if (Object.prototype.hasOwnProperty.call(LINE_HERO_COLOR, k) && s.indexOf(k) !== -1) return LINE_HERO_COLOR[k];
    }
    return null;
  }

  /* Perceived lightness 0–255, used to lead each card with its lightest colourway. */
  function lightness(hex) {
    var rgb = hexToRgb(hex);
    if (!rgb) return -1;
    return 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2];
  }

  /* ---------- rendering ---------- */
  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  function App(root) {
    this.root = root;
    this.cfg = {
      domain: root.dataset.domain,
      token: root.dataset.token,
      version: root.dataset.version || "2025-07",
      handle: root.dataset.collection,
      activeTags: (function () {
        // Prefer Liquid's collection.current_tags; fall back to parsing the tag
        // segment from a /collections/<handle>/<tag1+tag2> URL.
        var fromAttr = (root.dataset.activeTags || "").split(",").map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
        if (fromAttr.length) return fromAttr;
        var handle = (root.dataset.collection || "").toLowerCase();
        var base = "/collections/" + handle + "/";
        var path = (location.pathname || "").toLowerCase();
        var i = path.indexOf(base);
        if (i === -1) return [];
        var rest = path.slice(i + base.length).split("?")[0].replace(/\/+$/, "");
        if (!rest) return [];
        return decodeURIComponent(rest).split("+").map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
      })(),
      imageSwatches: root.dataset.imageSwatches === "true",
      groupBy: root.dataset.groupBy || "fabric",
      layout: root.dataset.layout || "grid",
      showLifestyle: root.dataset.lifestyleShow !== "false",
      lifestyle: (root.dataset.lifestyle || "").split("|").filter(Boolean)
    };
    this.mount = root.querySelector("[data-lls-grid]");
    this.filterBar = root.querySelector("[data-lls-filters]");
    this.lines = [];
    this.filters = { fabric: [], color: [], size: [], threadCount: [], priceMin: null, priceMax: null };
    this.sort = "Featured";
  }

  App.prototype.fetch = function () {
    var self = this;
    var node = "node{\n      id title description handle tags createdAt\n      priceRange{ minVariantPrice{ amount currencyCode } }\n      colorName: metafield(namespace:\"custom\", key:\"product_color_name\"){ value }\n      productsColor: metafield(namespace:\"custom\", key:\"products_color\"){ value }\n      rating: metafield(namespace:\"reviews\", key:\"rating\"){ value }\n      ratingCount: metafield(namespace:\"reviews\", key:\"rating_count\"){ value }\n      images(first:4){ edges{ node{ url altText } } }\n      variants(first:20){ edges{ node{ id title availableForSale price{ amount currencyCode } selectedOptions{ name value } } } } }";
    var q = "query($handle:String!,$first:Int!,$after:String){ collection(handle:$handle){ products(first:$first, after:$after){ pageInfo{ hasNextPage endCursor } edges{ " + node + " } } } }";
    var url = "https://" + this.cfg.domain + "/api/" + this.cfg.version + "/graphql.json";
    var token = this.cfg.token, handle = this.cfg.handle, all = [];
    function page(after) {
      return fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": token },
        body: JSON.stringify({ query: q, variables: { handle: handle, first: 250, after: after } })
      }).then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      }).then(function (data) {
        if (data.errors) throw new Error(data.errors.map(function (e) { return e.message; }).join(", "));
        var conn = data.data && data.data.collection && data.data.collection.products;
        var edges = (conn && conn.edges) || [];
        all = all.concat(edges);
        var pi = conn && conn.pageInfo;
        if (pi && pi.hasNextPage && all.length < 2000) return page(pi.endCursor);
      });
    }
    /* Non-English pages: grouping/sorting/colour logic keeps running on the
       English titles above; this second, light query only fetches the
       translated titles for display. Any failure falls back to English. */
    function names(after) {
      if (!window.LL_LANG) return Promise.resolve();
      var nq = "query($handle:String!,$first:Int!,$after:String) @inContext(language: " + window.LL_LANG + "){ collection(handle:$handle){ products(first:$first, after:$after){ pageInfo{ hasNextPage endCursor } edges{ node{ id title } } } } }";
      return fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": token },
        body: JSON.stringify({ query: nq, variables: { handle: handle, first: 250, after: after } })
      }).then(function (r) { return r.json(); }).then(function (data) {
        var conn = data && data.data && data.data.collection && data.data.collection.products;
        ((conn && conn.edges) || []).forEach(function (e) { if (e.node) LLNAMES[e.node.id] = e.node.title; });
        var pi = conn && conn.pageInfo;
        if (pi && pi.hasNextPage) return names(pi.endCursor);
      }).catch(function () {});
    }
    return page(null).then(function () { return names(null); }).then(function () {
      // Honor Shopify tag-filtered collection URLs (e.g. /collections/x/set-of-4).
      // The Storefront API returns the whole collection, so filter to products
      // carrying every active tag. No active tags → unchanged (full collection).
      var active = self.cfg.activeTags;
      if (active && active.length) {
        all = all.filter(function (e) {
          var t = ((e.node && e.node.tags) || []).map(function (x) { return String(x).toLowerCase(); });
          return active.every(function (tag) { return t.indexOf(tag) !== -1; });
        });
      }
      self.lines = groupByLine(all);
      self.buildFacetOptions();
      self.render();
    });
  };

  App.prototype.buildFacetOptions = function () {
    var fabrics = {}, sizes = {}, tcs = {}, presentMains = {};
    this.colorMainOf = {};
    var self = this;
    this.lines.forEach(function (line) {
      fabrics[line.fabricLabel] = true;
      if (line.threadCount) tcs[line.threadCount] = true;
      line.colors.forEach(function (c) {
        var main = mainForColor(c.colorName, c.hex);
        if (main) { self.colorMainOf[c.colorName.toLowerCase()] = main; presentMains[main] = true; }
      });
      line.representative.node.variants.edges.forEach(function (v) {
        v.node.selectedOptions.forEach(function (o) {
          if (o.name.toLowerCase() === "size") sizes[normalizeSize(o.value)] = true;
        });
      });
    });
    // Only show main-colour buckets that actually contain a product in this collection
    this.mainColors = MAIN_COLORS.filter(function (m) { return presentMains[m.name]; });
    this.facetOptions = {
      fabric: Object.keys(fabrics),
      color: this.mainColors.map(function (m) { return m.name; }),
      size: Object.keys(sizes),
      threadCount: Object.keys(tcs).map(Number).sort(function (a, b) { return a - b; })
    };
    this.renderFacetBar();
  };

  App.prototype.wantedColorSet = function () {
    if (!this.filters.color.length) return null;
    var cmap = this.colorMainOf || {};
    var sel = this.filters.color;
    // returns a predicate-friendly lookup: colour name -> true when its bucket is selected
    var w = {};
    Object.keys(cmap).forEach(function (nameLower) {
      if (sel.indexOf(cmap[nameLower]) !== -1) w[nameLower] = true;
    });
    return w;
  };

  /* Does `line` carry facet value `opt` for facet `key`? */
  App.prototype.lineHasOption = function (line, key, opt) {
    if (key === "fabric") return line.fabricLabel === opt;
    if (key === "threadCount") return String(line.threadCount) === opt;
    if (key === "color") {
      var cmap = this.colorMainOf || {};
      return line.colors.some(function (c) { return cmap[c.colorName.toLowerCase()] === opt; });
    }
    if (key === "size") {
      return line.representative.node.variants.edges.some(function (v) {
        return v.node.selectedOptions.some(function (o) {
          return o.name.toLowerCase() === "size" && normalizeSize(o.value) === opt;
        });
      });
    }
    return false;
  };

  /* How many lines would show if `opt` were added to facet `key`, given every
     other active filter. Options are OR'd within a facet, so the facet's own
     selections are ignored. Used to disable choices that lead to "No products found". */
  App.prototype.optionCount = function (key, opt) {
    var self = this;
    var saved = this.filters[key];
    this.filters[key] = [];
    var n = this.lines.filter(function (l) { return self.lineMatches(l) && self.lineHasOption(l, key, opt); }).length;
    this.filters[key] = saved;
    return n;
  };

  App.prototype.refreshFacetAvailability = function () {
    var self = this;
    if (!this.filterBar) return;
    Array.prototype.forEach.call(this.filterBar.querySelectorAll("[data-lls-key]"), function (node) {
      var key = node.getAttribute("data-lls-key");
      var opt = node.getAttribute("data-lls-opt");
      var active = self.filters[key].indexOf(opt) !== -1;
      var empty = !active && self.optionCount(key, opt) === 0;
      node.classList.toggle("is-disabled", empty);
      var input = node.tagName === "BUTTON" ? node : node.querySelector("input");
      input.disabled = empty;
    });
  };

  /* Variants of `node` in the sizes the shopper filtered to (all variants when
     no size filter is on). */
  App.prototype.sizedVariants = function (node) {
    var sizes = this.filters.size;
    if (!sizes.length) return node.variants.edges;
    return node.variants.edges.filter(function (v) {
      return v.node.selectedOptions.some(function (o) {
        return o.name.toLowerCase() === "size" && sizes.indexOf(normalizeSize(o.value)) !== -1;
      });
    });
  };

  /* Variant a card links to and adds to bag: first available in the filtered size. */
  App.prototype.pickVariant = function (node) {
    var edges = this.sizedVariants(node);
    if (!edges.length) edges = node.variants.edges;
    return edges.filter(function (e) { return e.node.availableForSale; })[0] || edges[0];
  };

  /* Lowest price across the line's colours, in the filtered size(s) when a size
     filter is on, so a Queen filter shows, filters and sorts by Queen prices. */
  App.prototype.linePrice = function (line) {
    if (!this.filters.size.length) return line.minPrice;
    var self = this, best = null;
    line.colors.forEach(function (c) {
      self.sizedVariants(c.product.node).forEach(function (v) {
        if (!best || parseFloat(v.node.price.amount) < parseFloat(best.amount)) best = v.node.price;
      });
    });
    return best || line.minPrice;
  };

  App.prototype.lineMatches = function (line) {
    var f = this.filters;
    if (f.fabric.length && f.fabric.indexOf(line.fabricLabel) === -1) return false;
    if (f.threadCount.length && f.threadCount.indexOf(String(line.threadCount)) === -1) return false;
    if (f.color.length) {
      var cmap = this.colorMainOf || {};
      var hasColor = line.colors.some(function (c) { return f.color.indexOf(cmap[c.colorName.toLowerCase()]) !== -1; });
      if (!hasColor) return false;
    }
    if (f.size.length) {
      var hasSize = line.representative.node.variants.edges.some(function (v) {
        return v.node.selectedOptions.some(function (o) {
          return o.name.toLowerCase() === "size" && f.size.indexOf(normalizeSize(o.value)) !== -1;
        });
      });
      if (!hasSize) return false;
    }
    var price = parseFloat(this.linePrice(line).amount);
    if (f.priceMin != null && price < f.priceMin) return false;
    if (f.priceMax != null && price > f.priceMax) return false;
    return true;
  };

  App.prototype.visibleLines = function () {
    var self = this;
    var lines = this.lines.filter(function (l) { return self.lineMatches(l); });
    lines = lines.slice().sort(function (a, b) {
      if (self.sort === "Price: Low to High") return parseFloat(self.linePrice(a).amount) - parseFloat(self.linePrice(b).amount);
      if (self.sort === "Price: High to Low") return parseFloat(self.linePrice(b).amount) - parseFloat(self.linePrice(a).amount);
      if (self.sort === "Newest") return new Date(b.representative.node.createdAt) - new Date(a.representative.node.createdAt);
      var r = rankBase(a.baseTitle) - rankBase(b.baseTitle);
      if (r !== 0) return r;
      var f = featuredRank(a.baseTitle) - featuredRank(b.baseTitle);
      return f !== 0 ? f : a.baseTitle.localeCompare(b.baseTitle);
    });
    return lines;
  };

  App.prototype.render = function () {
    var self = this;
    this.mount.innerHTML = "";
    var lines = this.visibleLines();
    if (!lines.length) {
      this.mount.appendChild(el("div", "lls-empty",
        '<p class="lls-empty__title">' + llT("No products found") + '</p>' +
        '<p class="lls-empty__body">' + llT("Try clearing a filter to see more of the collection.") + '</p>'));
      return;
    }
    // group visible lines, preserving order. Mode: fabric (default) | category | none
    var groups = [], gmap = {};
    var mode = this.cfg.groupBy;
    if (mode === "none") {
      groups = [{ label: "", lines: lines }];
    } else {
      var byCat = mode === "category";
      lines.forEach(function (l) {
        var key = byCat ? l.categoryLabel : l.fabricLabel;
        if (!gmap[key]) { gmap[key] = { label: key, lines: [] }; groups.push(gmap[key]); }
        gmap[key].lines.push(l);
      });
      // Percale (the organic line) leads; fabrics not listed keep their order after these.
      var ORD = byCat ? ["Sheets", "Towels", "Beach", "Pillowcases", "More"] : ["Percale Weave", "Sateen Weave", "Flannel"];
      groups.sort(function (a, b) {
        var ia = ORD.indexOf(a.label), ib = ORD.indexOf(b.label);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
    }
    var single = groups.length === 1;
    groups.forEach(function (g, i) { self.mount.appendChild(self.renderFamily(g, i, single)); });
  };

  App.prototype.renderFamily = function (group, index, hideHeader) {
    var self = this;
    var carousel = this.cfg.layout === "carousel";
    var section = el("section", "lls-family");
    var inner = el("div", "lls-family__inner");

    var prev, next;
    if (!hideHeader || carousel) {
      var head = el("div", "lls-family__head");
      if (!hideHeader) head.appendChild(el("h3", "lls-family__title", llLabel(group.label)));
      var right = el("div", "lls-family__meta");
      if (!hideHeader) right.appendChild(el("span", "lls-family__count", llT(group.lines.length === 1 ? "{n} style" : "{n} styles", group.lines.length)));
      if (carousel && group.lines.length > 1) {
        var arrows = el("div", "lls-arrows");
        prev = el("button", "lls-arrow lls-arrow--prev", "‹"); prev.type = "button"; prev.setAttribute("aria-label", "Scroll left");
        next = el("button", "lls-arrow lls-arrow--next", "›"); next.type = "button"; next.setAttribute("aria-label", "Scroll right");
        arrows.appendChild(prev); arrows.appendChild(next);
        right.appendChild(arrows);
      }
      head.appendChild(right);
      inner.appendChild(head);
    }

    var container;
    if (carousel) {
      // The lifestyle tile is a fixed anchor beside the track, not a slide in it,
      // so the arrows scroll only the product cards and the photo stays put.
      var track = el("div", "lls-track");
      group.lines.forEach(function (line) { track.appendChild(self.renderCard(line)); });

      var clife = this.cfg.showLifestyle && this.cfg.lifestyle.length ? this.cfg.lifestyle[index % this.cfg.lifestyle.length] : null;
      if (clife) {
        container = el("div", "lls-row");
        var clt = el("div", "lls-lifestyle");
        var cim = document.createElement("img"); cim.src = clife; cim.loading = "lazy"; cim.alt = "";
        clt.appendChild(cim);
        container.appendChild(clt);
        container.appendChild(track);
      } else {
        container = track;
      }

      if (prev && next) {
        var upd = function () {
          var max = track.scrollWidth - track.clientWidth - 2;
          prev.disabled = track.scrollLeft <= 2;
          next.disabled = track.scrollLeft >= max;
        };
        prev.addEventListener("click", function () { track.scrollBy({ left: -track.clientWidth * 0.9, behavior: "smooth" }); });
        next.addEventListener("click", function () { track.scrollBy({ left: track.clientWidth * 0.9, behavior: "smooth" }); });
        track.addEventListener("scroll", upd, { passive: true });
        window.addEventListener("resize", upd);
        setTimeout(upd, 60);
      }
    } else {
      container = el("div", "lls-grid");
      var life = this.cfg.showLifestyle && this.cfg.lifestyle.length ? this.cfg.lifestyle[index % this.cfg.lifestyle.length] : null;
      if (life) {
        var lt = el("div", "lls-lifestyle");
        var im = document.createElement("img"); im.src = life; im.loading = "lazy"; im.alt = "";
        lt.appendChild(im);
        container.appendChild(lt);
      }
      group.lines.forEach(function (line) { container.appendChild(self.renderCard(line)); });
    }
    inner.appendChild(container);
    section.appendChild(inner);
    return section;
  };

  App.prototype.renderCard = function (line) {
    var self = this;
    var state = { idx: 0 };
    var card = el("article", "lls-card");

    var media = el("a", "lls-card__media");
    var img = document.createElement("img");
    img.loading = "lazy";
    media.appendChild(img);
    /* Some colourways are photographed much wider than square (e.g. 450 TC Ivory is
       435x316 against the usual 870x820). object-fit:cover would crop and enlarge
       those; show them whole instead so the stack isn't zoomed and clipped. */
    function fitMedia() {
      var w = img.naturalWidth, h = img.naturalHeight;
      if (!w || !h) return;
      var ar = w / h;
      media.classList.toggle("is-contain", ar > 1.15 || ar < 0.87);
    }
    img.addEventListener("load", fitMedia);
    // Hover image: fades in over the main shot on devices that can hover.
    var hoverImg = document.createElement("img");
    hoverImg.className = "lls-card__hover";
    hoverImg.loading = "lazy";
    hoverImg.alt = "";
    hoverImg.setAttribute("aria-hidden", "true");
    media.appendChild(hoverImg);
    var badges = el("div", "lls-badges");
    if (isBestSeller(line.baseTitle)) badges.appendChild(el("span", "lls-badge lls-badge--best", llT("Best Seller")));
    if (line.threadCount) badges.appendChild(el("span", "lls-badge", line.threadCount + llT(" TC")));
    if (badges.childNodes.length) media.appendChild(badges);
    var addBtn = el("button", "lls-card__add", llT("Add to bag"));
    addBtn.type = "button";
    media.appendChild(addBtn);
    card.appendChild(media);

    var body = el("div", "lls-card__body");
    var titleLink = el("a", "lls-card__title", line.displayBase || displayTitle(line.baseTitle));
    body.appendChild(titleLink);
    var priceEl = el("p", "lls-card__price", llT("From") + " " + money(line.minPrice.amount, line.minPrice.currencyCode));
    body.appendChild(priceEl);

    if (line.rating != null && line.rating > 0) {
      var pct = Math.max(0, Math.min(100, (line.rating / 5) * 100));
      var stars = el("div", "lls-rating");
      stars.innerHTML =
        '<span class="lls-stars"><span class="lls-stars__bg">★★★★★</span>' +
        '<span class="lls-stars__on" style="width:' + pct.toFixed(1) + '%">★★★★★</span></span>' +
        (line.ratingCount ? '<span class="lls-rating__count">(' + line.ratingCount + ')</span>' : "");
      body.appendChild(stars);
    }

    var swatches = el("div", "lls-swatches");
    // Colours whose hex is a #ffffff placeholder (patterns/prints) get an image
    // swatch (a thumbnail of the product) instead of a blank white circle.
    function isWhite(hex) {
      var h = String(hex || "").trim().toLowerCase().replace(/\s/g, "");
      return h === "#ffffff" || h === "#fff" || h === "ffffff" || h === "fff";
    }
    function thumbFor(c) {
      var edges = c.product.node.images.edges;
      var u = edges[0] && edges[0].node.url;
      if (!u) return "";
      return u + (u.indexOf("?") === -1 ? "?" : "&") + "width=80";
    }
    var colors = line.colors.slice();
    if (!self.cfg.imageSwatches) {
      // Sheets/Bath: hide #ffffff placeholder swatches (keep only real colours)
      var solid = colors.filter(function (c) { return !isWhite(c.hex); });
      if (solid.length) colors = solid;
    }
    // Show the collection at its lightest: order colourways light -> dark so the
    // card image and the first swatches are the pale tones, not the charcoals.
    colors = colors.slice().sort(function (a, b) { return lightness(b.hex) - lightness(a.hex); });
    // A line can pin a specific colourway as its hero (e.g. Egyptian Cotton -> Mineral).
    var heroWant = heroColorFor(line.baseTitle);
    if (heroWant) {
      for (var hi = 0; hi < colors.length; hi++) {
        if (colors[hi].colorName.toLowerCase().indexOf(heroWant) !== -1) {
          if (hi > 0) colors.unshift(colors.splice(hi, 1)[0]);
          break;
        }
      }
    }
    // If a colour filter is active, surface the matching colour(s) first so the
    // card image + swatches reflect what was filtered.
    var wanted = self.wantedColorSet();
    if (wanted) {
      colors = colors.slice().sort(function (a, b) {
        return (wanted[a.colorName.toLowerCase()] ? 0 : 1) - (wanted[b.colorName.toLowerCase()] ? 0 : 1);
      });
    }
    var shown = colors.slice(0, 5);
    var extra = colors.length - shown.length;

    function sync() {
      var active = colors[state.idx] || colors[0];
      var node = active.product.node;
      var url = node.images.edges[0] && node.images.edges[0].node.url;
      if (url) img.src = url;
      img.alt = LLNAMES[node.id] || node.title;
      fitMedia();
      var alt = hoverImageUrl(node);
      if (alt) hoverImg.src = alt; else hoverImg.removeAttribute("src");
      media.classList.toggle("has-hover", !!alt);
      var v = self.pickVariant(node);
      var sized = self.filters.size.length && v;
      var handle = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/") + "products/" + node.handle + (sized ? "?variant=" + numericId(v.node.id) : "");
      media.setAttribute("href", handle);
      titleLink.setAttribute("href", handle);
      priceEl.textContent = sized
        ? money(v.node.price.amount, v.node.price.currencyCode)
        : llT("From") + " " + money(line.minPrice.amount, line.minPrice.currencyCode);
      Array.prototype.forEach.call(swatches.querySelectorAll(".lls-swatch"), function (s, i) {
        s.classList.toggle("is-active", i === state.idx);
      });
      addBtn.dataset.variant = v ? numericId(v.node.id) : "";
      addBtn.disabled = !v;
    }

    if (colors.length > 1) {
      shown.forEach(function (c, i) {
        var s = el("button", "lls-swatch");
        s.type = "button";
        if (self.cfg.imageSwatches && isWhite(c.hex)) {
          var thumb = thumbFor(c);
          if (thumb) { s.classList.add("lls-swatch--img"); s.style.backgroundImage = "url('" + thumb + "')"; }
          else { s.style.backgroundColor = "#f0eee4"; }
        } else {
          s.style.backgroundColor = c.hex;
        }
        s.setAttribute("aria-label", c.colorName);
        s.addEventListener("click", function () { state.idx = i; sync(); });
        swatches.appendChild(s);
      });
      if (extra > 0) swatches.appendChild(el("span", "lls-swatch__more", "+ " + extra + " more"));
      body.appendChild(swatches);
    }
    card.appendChild(body);

    addBtn.addEventListener("click", function () {
      if (!addBtn.dataset.variant) return;
      self.addToCart(addBtn.dataset.variant, addBtn);
    });

    sync();
    return card;
  };

  /* ---------- add to cart via Dawn ---------- */
  App.prototype.addToCart = function (variantId, btn) {
    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = llT("Adding…");
    var sections = "";
    var drawer = document.querySelector("cart-drawer");
    if (drawer && drawer.dataset && drawer.id) sections = "cart-drawer,cart-icon-bubble";
    /* Locale-aware endpoint so the re-rendered cart drawer comes back in the page's language. */
    fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : "/cart/add") + ".js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sections ? sections.split(",") : undefined })
    }).then(function (r) { return r.json(); }).then(function (res) {
      btn.textContent = llT("Added ✓");
      // refresh Dawn cart drawer sections if present
      if (res.sections) {
        if (res.sections["cart-drawer"]) {
          var d = document.querySelector("cart-drawer");
          if (d) {
            var parsed = new DOMParser().parseFromString(res.sections["cart-drawer"], "text/html");
            var fresh = parsed.querySelector("cart-drawer .drawer__inner") || parsed.querySelector("cart-drawer");
            var target = d.querySelector(".drawer__inner");
            if (fresh && target) target.innerHTML = fresh.innerHTML;
            d.classList.remove("is-empty");
            if (typeof d.open === "function") d.open();
          }
        }
        if (res.sections["cart-icon-bubble"]) {
          var bubble = document.getElementById("cart-icon-bubble");
          if (bubble) {
            var pb = new DOMParser().parseFromString(res.sections["cart-icon-bubble"], "text/html");
            var nb = pb.querySelector("#cart-icon-bubble") || pb.body.firstElementChild;
            if (nb) bubble.innerHTML = nb.innerHTML;
          }
        }
      } else {
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
      setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1600);
    }).catch(function () {
      btn.textContent = llT("Try again");
      setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1600);
    });
  };

  /* ---------- facet bar ---------- */
  App.prototype.renderFacetBar = function () {
    if (!this.filterBar) return;
    var self = this;
    var defs = [
      { key: "fabric", label: llT("Fabric"), options: this.facetOptions.fabric },
      { key: "color", label: llT("Color"), options: this.facetOptions.color, colorGrid: true },
      { key: "size", label: llT("Size"), options: this.facetOptions.size },
      { key: "price", label: llT("Price"), price: true },
      { key: "threadCount", label: llT("Thread Count"), options: this.facetOptions.threadCount.map(function (n) { return String(n); }), suffix: llT(" TC") }
    ];
    var group = this.filterBar.querySelector("[data-lls-facet-group]");
    group.innerHTML = "";
    defs.forEach(function (def) {
      if (!def.price && (!def.options || !def.options.length)) return;
      if (def.key === "color" && def.options.length < 2) return; // a 1-swatch colour filter is pointless
      if (def.key === "fabric" && def.options.length < 2) return; // hide single-fabric filter
      group.appendChild(self.buildFacet(def));
    });
    this.updateChips();
    this.refreshFacetAvailability();
  };

  App.prototype.buildFacet = function (def) {
    var self = this;
    var wrap = el("div", "lls-facet");
    var btn = el("button", "lls-facet__btn");
    btn.type = "button";
    btn.setAttribute("aria-expanded", "false");
    btn.innerHTML = def.label + ' <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="m6 9 6 6 6-6"/></svg>';
    var menu = el("div", "lls-menu");
    menu.hidden = true;

    if (def.colorGrid) {
      menu.classList.add("lls-menu--colors");
      var grid = el("div", "lls-cgrid");
      def.options.forEach(function (opt) {
        var m = MAIN_BY_NAME[opt] || { hex: "#c8bfb5" };
        var tile = el("button", "lls-cswatch");
        tile.type = "button";
        tile.setAttribute("data-lls-key", "color");
        tile.setAttribute("data-lls-opt", opt);
        if (self.filters.color.indexOf(opt) !== -1) tile.classList.add("is-active");
        var box = el("span", "lls-cswatch__box");
        if (m.multi) box.classList.add("lls-cswatch__box--multi");
        else box.style.backgroundColor = m.hex;
        tile.appendChild(box);
        tile.appendChild(el("span", "lls-cswatch__label", llT(opt)));
        tile.addEventListener("click", function () {
          var arr = self.filters.color;
          if (arr.indexOf(opt) !== -1) self.filters.color = arr.filter(function (x) { return x !== opt; });
          else arr.push(opt);
          tile.classList.toggle("is-active");
          self.afterChange(btn);
        });
        grid.appendChild(tile);
      });
      menu.appendChild(grid);
    } else if (def.price) {
      menu.innerHTML =
        '<div class="lls-menu__price"><input type="number" inputmode="numeric" placeholder="' + llT("Min") + '" data-min><span>–</span><input type="number" inputmode="numeric" placeholder="' + llT("Max") + '" data-max></div>' +
        '<button type="button" class="lls-menu__apply">' + llT("Apply") + '</button>';
      menu.querySelector(".lls-menu__apply").addEventListener("click", function () {
        var mn = menu.querySelector("[data-min]").value;
        var mx = menu.querySelector("[data-max]").value;
        self.filters.priceMin = mn === "" ? null : Number(mn);
        self.filters.priceMax = mx === "" ? null : Number(mx);
        self.closeMenus(); self.afterChange(btn);
      });
    } else {
      def.options.forEach(function (opt) {
        var label = el("label");
        label.setAttribute("data-lls-key", def.key);
        label.setAttribute("data-lls-opt", opt);
        var cb = document.createElement("input");
        cb.type = "checkbox"; cb.value = opt;
        cb.checked = self.filters[def.key].indexOf(opt) !== -1;
        cb.addEventListener("change", function () {
          var arr = self.filters[def.key];
          if (cb.checked) { if (arr.indexOf(opt) === -1) arr.push(opt); }
          else { self.filters[def.key] = arr.filter(function (x) { return x !== opt; }); }
          self.afterChange(btn);
        });
        label.appendChild(cb);
        if (def.swatch) {
          var sw = el("span", "");
          sw.style.cssText = "display:inline-block;width:14px;height:14px;border-radius:50%;border:1px solid var(--lls-border);background:" + (self.colorHex[opt] || "#c8bfb5");
          label.appendChild(sw);
        }
        label.appendChild(document.createTextNode(llLabel(opt) + (def.suffix || "")));
        menu.appendChild(label);
      });
    }

    btn.addEventListener("click", function (e) {
      e.stopPropagation();
      var isOpen = !menu.hidden;
      self.closeMenus();
      if (!isOpen) { menu.hidden = false; btn.setAttribute("aria-expanded", "true"); }
    });
    wrap.appendChild(btn);
    wrap.appendChild(menu);
    return wrap;
  };

  App.prototype.closeMenus = function () {
    Array.prototype.forEach.call(this.root.querySelectorAll(".lls-menu"), function (m) { m.hidden = true; });
    Array.prototype.forEach.call(this.root.querySelectorAll(".lls-facet__btn"), function (b) { b.setAttribute("aria-expanded", "false"); });
  };

  App.prototype.afterChange = function (btn) {
    var key = null;
    // mark active state on facet buttons by recomputing chips
    this.updateChips();
    this.refreshFacetAvailability();
    this.render();
  };

  App.prototype.activeFilterList = function () {
    var f = this.filters, out = [];
    ["fabric", "color", "size", "threadCount"].forEach(function (k) {
      f[k].forEach(function (v) { out.push({ key: k, value: v, label: k === "threadCount" ? v + llT(" TC") : v }); });
    });
    if (f.priceMin != null || f.priceMax != null) {
      out.push({ key: "price", value: "price", label: "$" + (f.priceMin != null ? f.priceMin : 0) + (f.priceMax != null ? "–$" + f.priceMax : "+") });
    }
    return out;
  };

  App.prototype.updateChips = function () {
    var self = this;
    var bar = this.root.querySelector("[data-lls-active]");
    if (!bar) return;
    var active = this.activeFilterList();
    bar.innerHTML = "";
    if (!active.length) { bar.style.display = "none"; return; }
    bar.style.display = "flex";
    active.forEach(function (a) {
      var chip = el("span", "lls-active__chip", llLabel(a.label) + ' <button type="button" aria-label="' + llT("Remove") + '">×</button>');
      chip.querySelector("button").addEventListener("click", function () {
        if (a.key === "price") { self.filters.priceMin = null; self.filters.priceMax = null; }
        else self.filters[a.key] = self.filters[a.key].filter(function (x) { return x !== a.value; });
        self.renderFacetBar(); self.render();
      });
      bar.appendChild(chip);
    });
    var clear = el("button", "lls-active__clear", llT("Clear all"));
    clear.addEventListener("click", function () {
      self.filters = { fabric: [], color: [], size: [], threadCount: [], priceMin: null, priceMax: null };
      self.renderFacetBar(); self.render();
    });
    bar.appendChild(clear);
  };

  App.prototype.initSort = function () {
    var self = this;
    var sortWrap = this.root.querySelector("[data-lls-sort]");
    if (!sortWrap) return;
    var btn = sortWrap.querySelector("[data-lls-sort-btn]");
    var menu = sortWrap.querySelector("[data-lls-sort-menu]");
    var label = sortWrap.querySelector("[data-lls-sort-label]");
    btn.addEventListener("click", function (e) {
      e.stopPropagation();
      var open = !menu.hidden; self.closeMenus(); menu.hidden = open;
    });
    Array.prototype.forEach.call(menu.querySelectorAll("[data-sort-opt]"), function (opt) {
      opt.addEventListener("click", function () {
        self.sort = opt.dataset.sortOpt;
        label.textContent = llT(self.sort);
        Array.prototype.forEach.call(menu.querySelectorAll("[data-sort-opt]"), function (o) { o.classList.remove("is-active"); });
        opt.classList.add("is-active");
        menu.hidden = true;
        self.render();
      });
    });
  };

  App.prototype.applyFabric = function (token) {
    if (!this.facetOptions) return;
    var t = String(token || "").toLowerCase().trim();
    if (!t) return;
    var opts = this.facetOptions.fabric;
    var matches = opts.filter(function (lbl) { return lbl.toLowerCase().indexOf(t) !== -1; });
    if (!matches.length) matches = opts.filter(function (lbl) { return t.indexOf(lbl.toLowerCase()) !== -1; });
    // toggle: if this exact fabric set is already the active filter, clear it
    var same = matches.length && matches.length === this.filters.fabric.length &&
      matches.every(function (m) { return this.filters.fabric.indexOf(m) !== -1; }, this);
    this.filters.fabric = same ? [] : matches;
    this.syncFabricTiles();
    this.renderFacetBar();
    this.render();
    var lu = this.root.querySelector(".lls-lineup");
    if (lu) lu.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  App.prototype.syncFabricTiles = function () {
    var active = this.filters.fabric;
    Array.prototype.forEach.call(this.root.querySelectorAll(".lls-fabric__card[data-fabric]"), function (card) {
      var t = card.getAttribute("data-fabric").toLowerCase();
      var on = active.some(function (lbl) { return lbl.toLowerCase().indexOf(t) !== -1 || t.indexOf(lbl.toLowerCase()) !== -1; });
      card.classList.toggle("is-active", !!on);
    });
  };

  App.prototype.initFabricTiles = function () {
    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll(".lls-fabric__card[data-fabric]"), function (card) {
      card.addEventListener("click", function () { self.applyFabric(card.getAttribute("data-fabric")); });
    });
  };

  App.prototype.initFabricCarousel = function () {
    var track = this.root.querySelector("[data-lls-fabric-track]");
    if (!track) return;
    var prev = this.root.querySelector("[data-lls-fabric-prev]");
    var next = this.root.querySelector("[data-lls-fabric-next]");
    function by(d) { track.scrollBy({ left: d * track.clientWidth * 0.8, behavior: "smooth" }); }
    if (prev) prev.addEventListener("click", function () { by(-1); });
    if (next) next.addEventListener("click", function () { by(1); });

    /* With only a couple of fabric tiles the track never overflows, so the arrows
       are dead controls sitting in the header. Hide the pair unless there is
       actually something to scroll to, and grey them out at each end. */
    var arrows = prev && prev.parentNode;
    function sync() {
      var scrollable = track.scrollWidth - track.clientWidth > 2;
      if (arrows) arrows.style.display = scrollable ? "" : "none";
      if (!scrollable) return;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    }
    track.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    sync();
    setTimeout(sync, 200);
  };

  function boot(root) {
    var app = new App(root);
    root.__llsApp = app;
    app.initFabricCarousel();
    app.initFabricTiles();
    app.initSort();
    document.addEventListener("click", function () { app.closeMenus();
      var sm = root.querySelector("[data-lls-sort-menu]"); if (sm) sm.hidden = true; });
    app.mount.innerHTML = '<div class="lls-state"><span class="lls-spinner"></span></div>';
    app.fetch().catch(function (err) {
      console.error("[ll-sheets]", err);
      app.mount.innerHTML = '<div class="lls-empty"><p class="lls-empty__title">' + llT("Couldn't load products") + '</p><p class="lls-empty__body">' + llT("Please refresh in a moment.") + '</p></div>';
    });
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-lls-root]"), function (root) {
      if (root.dataset.llsReady) return;
      root.dataset.llsReady = "1";
      boot(root);
    });
  }
  if (document.readyState !== "loading") init();
  else document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();
